/**
 * useRouteWeather Hook
 *
 * Huvudlogik för att kombinera routing med väderdata.
 * Hämtar rutter från Google Directions API och väderprognos från SMHI
 * för punkter längs rutten baserat på beräknad ankomsttid.
 * Stöder alternativa rutter.
 */

import { useState, useCallback } from 'react';
import {
  getDirections,
  sampleRoutePoints,
  toGoogleLatLng,
} from '@/services/googlemaps';
import type { GoogleRoute } from '@/services/googlemaps';
import {
  fetchWeatherForecastHybrid,
  fetchWeatherWarnings,
  getForecastForTime,
  getWarningsForLocation,
} from '@/services/smhi';
import type {
  LatLng,
  WeatherRoute,
  RouteWeatherPoint,
  WeatherWarning,
  GoogleCoordinate,
  GeocodingResult,
} from '@/types';

interface UseRouteWeatherParams {
  origin: GeocodingResult | null;
  destination: GeocodingResult | null;
  departureTime: Date;
}

/**
 * Alternativ rutt (utan väderdata än)
 */
export interface RouteAlternative {
  index: number;
  summary: string;
  distance: number;
  duration: number;
  polyline: GoogleCoordinate[]; // För att visa på kartan
}

interface UseRouteWeatherResult {
  route: WeatherRoute | null;
  alternatives: RouteAlternative[];
  selectedRouteIndex: number;
  isLoading: boolean;
  error: Error | null;
  search: () => Promise<void>;
  selectRoute: (index: number) => Promise<void>;
  warnings: WeatherWarning[];
}

const AVERAGE_SPEED_KMH = 80; // Genomsnittlig hastighet för bil
const SAMPLE_INTERVAL_KM = 50; // Sampla väder var 50:e km

/**
 * Beräkna ankomsttid till en punkt baserat på avstånd och hastighet
 */
function calculateArrivalTime(
  departureTime: Date,
  distanceKm: number,
  averageSpeedKmh: number = AVERAGE_SPEED_KMH
): Date {
  const travelTimeHours = distanceKm / averageSpeedKmh;
  const travelTimeMs = travelTimeHours * 60 * 60 * 1000;
  return new Date(departureTime.getTime() + travelTimeMs);
}

/**
 * Beräkna ackumulerat avstånd till varje punkt
 */
function calculateDistances(coordinates: LatLng[]): number[] {
  const distances: number[] = [0];

  for (let i = 1; i < coordinates.length; i++) {
    const prev = coordinates[i - 1];
    const curr = coordinates[i];
    if (!prev || !curr) continue;

    const distance = haversineDistance(prev, curr);
    distances.push((distances[i - 1] || 0) + distance);
  }

  return distances;
}

/**
 * Haversine-formel för avstånd mellan två koordinater
 */
function haversineDistance(a: LatLng, b: LatLng): number {
  const R = 6371; // Jordens radie i km
  const dLat = toRad(b.lat - a.lat);
  const dLon = toRad(b.lon - a.lon);

  const sinLat = Math.sin(dLat / 2);
  const sinLon = Math.sin(dLon / 2);

  const calc = sinLat * sinLat +
    Math.cos(toRad(a.lat)) * Math.cos(toRad(b.lat)) * sinLon * sinLon;

  return R * 2 * Math.atan2(Math.sqrt(calc), Math.sqrt(1 - calc));
}

function toRad(deg: number): number {
  return deg * (Math.PI / 180);
}

/**
 * Bygg WeatherRoute från GoogleRoute
 */
async function buildWeatherRoute(
  routeData: GoogleRoute,
  origin: GeocodingResult,
  destination: GeocodingResult,
  departureTime: Date,
  allWarnings: WeatherWarning[]
): Promise<WeatherRoute> {
  // Sampla punkter längs rutten (var 50:e km)
  const sampledPoints = sampleRoutePoints(routeData.coordinates, SAMPLE_INTERVAL_KM);

  // Beräkna avstånd till varje samplingspunkt
  const distances = calculateDistances(sampledPoints);

  // Hämta väderprognos för varje punkt parallellt
  const weatherPromises = sampledPoints.map(async (location, index): Promise<RouteWeatherPoint | null> => {
    const distanceFromStart = distances[index] || 0;
    const estimatedArrival = calculateArrivalTime(departureTime, distanceFromStart);

    const forecastResult = await fetchWeatherForecastHybrid(location);

    if (!forecastResult.success) {
      console.warn(`Kunde inte hämta väder för punkt ${index}:`, forecastResult.error);
      return null;
    }

    const forecast = getForecastForTime(forecastResult.data, estimatedArrival);

    if (!forecast) {
      return null;
    }

    const pointWarnings = getWarningsForLocation(allWarnings, location, estimatedArrival);

    return {
      location,
      distanceFromStart,
      estimatedArrival,
      weather: forecast,
      warnings: pointWarnings,
    };
  });

  const weatherResults = await Promise.all(weatherPromises);
  const weatherPoints = weatherResults.filter((p): p is RouteWeatherPoint => p !== null);

  // Konvertera rutt-koordinater till Google Maps-format
  const polyline: GoogleCoordinate[] = routeData.coordinates.map(toGoogleLatLng);

  // Beräkna ankomsttid
  const totalDistanceKm = routeData.distance / 1000;
  const arrivalTime = calculateArrivalTime(departureTime, totalDistanceKm);

  return {
    origin: {
      location: origin.coordinates,
      name: origin.name,
    },
    destination: {
      location: destination.coordinates,
      name: destination.name,
    },
    totalDistance: routeData.distance,
    totalDuration: routeData.duration,
    departureTime,
    arrivalTime,
    weatherPoints,
    polyline,
  };
}

/**
 * Hook för att hämta väder längs en rutt med stöd för alternativa rutter
 */
export function useRouteWeather({
  origin,
  destination,
  departureTime,
}: UseRouteWeatherParams): UseRouteWeatherResult {
  const [route, setRoute] = useState<WeatherRoute | null>(null);
  const [alternatives, setAlternatives] = useState<RouteAlternative[]>([]);
  const [allRoutes, setAllRoutes] = useState<GoogleRoute[]>([]);
  const [selectedRouteIndex, setSelectedRouteIndex] = useState(0);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<Error | null>(null);
  const [warnings, setWarnings] = useState<WeatherWarning[]>([]);

  const search = useCallback(async () => {
    if (!origin || !destination) {
      setError(new Error('Ange både start och destination'));
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      // 1. Hämta rutter från Google Directions API
      const routeResult = await getDirections(
        origin.coordinates,
        destination.coordinates
      );

      if (!routeResult.success) {
        throw routeResult.error;
      }

      const { routes } = routeResult.data;
      setAllRoutes(routes);

      // 2. Skapa alternativ-lista med polylines för kartvisning
      const alts: RouteAlternative[] = routes.map((r, i) => ({
        index: i,
        summary: r.summary,
        distance: r.distance,
        duration: r.duration,
        polyline: r.coordinates.map(toGoogleLatLng),
      }));
      setAlternatives(alts);
      setSelectedRouteIndex(0);

      // 3. Hämta vädervarningar
      const warningsResult = await fetchWeatherWarnings();
      const allWarnings = warningsResult.success ? warningsResult.data : [];
      setWarnings(allWarnings);

      // 4. Bygg väderrutt för första alternativet
      const firstRoute = routes[0];
      if (firstRoute) {
        const weatherRoute = await buildWeatherRoute(
          firstRoute,
          origin,
          destination,
          departureTime,
          allWarnings
        );
        setRoute(weatherRoute);
      }
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Något gick fel'));
      setRoute(null);
      setAlternatives([]);
    } finally {
      setIsLoading(false);
    }
  }, [origin, destination, departureTime]);

  const selectRoute = useCallback(async (index: number) => {
    if (!origin || !destination || index < 0 || index >= allRoutes.length) {
      return;
    }

    const selectedRoute = allRoutes[index];
    if (!selectedRoute) return;

    setIsLoading(true);
    setSelectedRouteIndex(index);

    try {
      const weatherRoute = await buildWeatherRoute(
        selectedRoute,
        origin,
        destination,
        departureTime,
        warnings
      );
      setRoute(weatherRoute);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Kunde inte ladda rutten'));
    } finally {
      setIsLoading(false);
    }
  }, [origin, destination, departureTime, allRoutes, warnings]);

  return {
    route,
    alternatives,
    selectedRouteIndex,
    isLoading,
    error,
    search,
    selectRoute,
    warnings,
  };
}
